import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from 'react-query';

import { useServiceError } from '@/hooks';

import { PageRequest } from '@/core/request/PageRequest';

import { DEFAULT_ORDER, DEFAULT_ROWS } from '..';
import {
  activeInactiveUser,
  changePassword,
  createUser,
  getUser,
  getUserReduced,
  listUsers,
  listUsersReduced,
  updateUser,
  userPasswordRecovery,
  userTokenPl24,
  userValidateCode,
} from './Users.service';
import { notification } from 'antd';
import notificationError from '@/utils/functions/notificationError';
import { useIntl } from 'react-intl';
import { store } from '@/Root';
import { authOperations } from '@/state/ducks/auth';
import { UserDetailReduced } from './Users.types';
import { getUserFromLocalStorage } from '@/utils/user';

export function useUsers(params?: PageRequest) {
  const query = useInfiniteQuery(
    ['users', { ...params }],
    ({ pageParam }) => {
      const req: PageRequest = {
        nextToken: pageParam,
        order: params?.order || DEFAULT_ORDER,
        rows: params?.rows || DEFAULT_ROWS,
        ...params,
      };

      return listUsers(req);
    },
    {
      getNextPageParam: (lastPage) => lastPage.nextToken,
    },
  );

  useServiceError(query.error);

  return query;
}

export function useUsersReduced(params?: PageRequest, enabled = true) {
  const queryClient = useQueryClient();
  const chache = queryClient.getQueryData(['users-reduced', { ...params }]);

  return useInfiniteQuery(
    ['users-reduced', { ...params }],
    ({ pageParam }) => {
      const req: PageRequest = {
        nextToken: pageParam,
        order: params?.order || DEFAULT_ORDER,
        rows: params?.rows || DEFAULT_ROWS,
        ...params,
      };

      return listUsersReduced(req);
    },
    {
      getNextPageParam: (lastPage) => lastPage.nextToken,
      cacheTime: 10000,
      enabled: enabled && !chache,
      onError(error) {
        notificationError(error);
      },
    },
  );
}

export function useUser(id?: number) {
  const query = useQuery(['users', id], () => getUser(id), { enabled: !!id });

  useServiceError(query.error);

  return query;
}

export function useUserReduced() {
  const queryClient = useQueryClient();

  return useMutation(
    async (id: number) => {
      const cache = queryClient.getQueryData<UserDetailReduced>([
        'user-detail-reduced',
        id,
      ]);

      if (cache?.id === id) return cache;

      const data = await getUserReduced(id);

      return data;
    },
    {
      onSuccess(user) {
        queryClient.setQueryData(['user-detail-reduced', user.id], user);
      },
      onError(error) {
        notificationError(error);
      },
    },
  );
}

export function useCreateUser() {
  const queryClient = useQueryClient();

  return useMutation(createUser, {
    onSuccess(data) {
      queryClient.invalidateQueries(['users']);
      queryClient.resetQueries(['users-reduced']);

      notification.success({
        message: 'Cadastro de usuário',
        description: `Usuário "${data.login}" cadastrado com sucesso!`,
      });
    },
    onError(error) {
      notificationError(error, {
        translate: (errors) =>
          errors.map((err) =>
            err.replace('User already exists', 'Usuário já existe'),
          ),
      });
    },
  });
}

export function useUpdateUser() {
  const queryClient = useQueryClient();
  return useMutation(updateUser, {
    async onSuccess(data) {
      await queryClient.invalidateQueries(['users']);
      await queryClient.resetQueries(['users-reduced']);

      try {
        await store.dispatch(authOperations.handleRefreshToken());
      } catch {}

      notification.success({
        message: 'Atualização de usuário',
        description: `Usuário "${data.login}" atualizado com sucesso!`,
      });
    },

    onError(error, data) {
      notificationError(error, {
        translate: (errors) =>
          errors.map((err) =>
            err.includes('tb_user_login_key')
              ? `O login "${data.login}" já está em uso`
              : err,
          ),
      });
    },
  });
}

export function useUserPasswordRecovery() {
  return useMutation(userPasswordRecovery, {
    onSuccess() {
      notification.success({
        message: 'Recuperação de senha',
        description: `Foi enviado um e-mail com a nova senha`,
      });
    },
    onError(error) {
      notificationError(error, {
        translate: (errors) =>
          errors.map((err) =>
            err.replace('User not found:', 'Usuário inexistente:'),
          ),
      });
    },
  });
}

export function useChangePassword() {
  const intl = useIntl();

  return useMutation(changePassword, {
    onSuccess() {
      notification.success({
        message: intl.formatMessage({
          id: 'settings.account.accessData.notification.success.title',
        }),
        description: intl.formatMessage({
          id: 'settings.account.accessData.notification.success.description',
        }),
      });
    },
    onError(error) {
      notificationError(error, {
        translate: (errors) =>
          errors.map((err) =>
            err.replace('User already exists', 'Usuário já existe'),
          ),
      });
    },
  });
}

export function useUserValidateCode() {
  return useMutation(userValidateCode, {
    onSuccess() {
      notification.success({
        message: 'Conta ativada com sucesso!',
      });
    },
    onError(error) {
      notificationError(error);
    },
  });
}

export function useUserTokenPl24() {
  return useQuery(['userTokenPl24'], userTokenPl24);
}

export function useActiveInactiveUser() {
  const queryClient = useQueryClient();
  return useMutation(activeInactiveUser, {
    async onSuccess() {
      await queryClient.invalidateQueries(['users']);
      await queryClient.resetQueries(['users-reduced']);

      notification.success({
        message: 'Atualização de usuário',
        description: `Usuário atualizado com sucesso!`,
      });
    },

    onError(error) {
      notificationError(error);
    },
  });
}

export function useUserCurrent() {
  const userStorage = getUserFromLocalStorage();

  return useUser(userStorage.userId ? +userStorage.userId : undefined);
}
